import { Button } from '@/components/ui/button';
import { useEffect } from 'react';
import { SUPPORT_WHATSAPP_URL } from '../../../utils/Constants';

/**
 * Confirmation step shown before sending a patient to customer care to cancel
 * their subscription. Cancellation is never self-serve on the legacy platform,
 * so the only action is a hand-off to WhatsApp support.
 *
 * The WhatsApp CTA is deliberately a real anchor (via `Button asChild`) rather
 * than a button calling `window.open`: iOS Safari blocks programmatic
 * `window.open` outside a direct gesture and is unreliable at resolving the
 * `wa.me` universal link, whereas a plain anchor tap always works.
 */
const CancelSubscriptionModal: React.FC<{
  onClose: () => void;
  onContactSupport: () => void;
}> = ({ onClose, onContactSupport }) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] px-[20px]'
      onClick={onClose}
    >
      <div
        role='dialog'
        aria-modal='true'
        aria-labelledby='cancel-subscription-title'
        className='bg-white rounded-xl p-6 w-full max-w-md'
        onClick={(event) => event.stopPropagation()}
      >
        <div
          id='cancel-subscription-title'
          className='font-bold text-[20px] leading-[26px] mb-[12px]'
        >
          Vuoi disdire il tuo abbonamento?
        </div>
        <div className='font-normal text-[14px] leading-[20px] mb-[24px]'>
          Stai seguendo un percorso medico. Per procedere con la disdetta, mettiti in
          contatto con il nostro team di supporto su WhatsApp: ti aiuteremo a gestire la
          richiesta nel modo più sicuro per la tua salute.
        </div>
        <Button asChild size='form' className='text-[14px] whitespace-normal leading-tight px-4'>
          <a
            href={SUPPORT_WHATSAPP_URL}
            target='_blank'
            rel='noopener noreferrer'
            onClick={onContactSupport}
          >
            Contatta il supporto su WhatsApp
          </a>
        </Button>
        <Button
          variant='white'
          size='form'
          className='mt-[12px] text-[14px] whitespace-normal leading-tight px-4 text-black'
          onClick={onClose}
        >
          Torna indietro
        </Button>
      </div>
    </div>
  );
};

export default CancelSubscriptionModal;
